/**
 * The recommendation step: the aggregated Snapshot, the Profile and the
 * earlier recommendations go to Claude; an overview, profile inferences and
 * new recommendations come back and are written to the Sheet. Raw documents
 * are never sent here. See ARCHITECTURE.md, Pipeline step 6.
 */
import Anthropic from '@anthropic-ai/sdk'
import { betaZodOutputFormat } from '@anthropic-ai/sdk/helpers/beta/zod'
import * as z from 'zod/v4'
import { analyse, type Snapshot } from './analysis'
import { readTables } from './book'
import { finish } from './claude'
import { tokens, type Meter } from './usage'
import { formatDate, formatTimestamp } from './dates'
import type { Database, Row } from './google/sheets'

export const ADVICE_MODEL = 'claude-opus-5-5'

export const AREAS = ['investing', 'retirement', 'tax', 'cash', 'debt', 'spending', 'cards', 'insurance', 'estate', 'data'] as const
export const PRIORITIES = ['high', 'medium', 'low'] as const
export const TONES = ['strength', 'concern', 'note'] as const

const AdviceSchema = z.object({
  overview: z
    .string()
    .describe('One paragraph, four to seven sentences: the overall position, the two or three things that matter most, with figures'),
  findings: z
    .array(
      z.object({
        tone: z.enum(TONES),
        title: z.string().describe('A short headline with the figure, e.g. "Savings rate 31%"'),
        detail: z.string().describe('One or two sentences: what it means for this household'),
      }),
    )
    .describe('Six to ten observations across the balance sheet, cash flow, investments, debt and risk'),
  profile: z.array(
    z.object({
      key: z.string().describe('snake_case, e.g. risk_tolerance, target_allocation, emergency_fund_target_months'),
      value: z.string(),
      basis: z.string().describe('What in the data this is based on'),
    }),
  ),
  recommendations: z.array(
    z.object({
      area: z.enum(AREAS),
      priority: z.enum(PRIORITIES),
      title: z.string().describe('One line, imperative'),
      detail: z.string().describe('The reasoning, with the figures it rests on and the trade-offs'),
      next_step: z.string().describe('The first concrete action, specific enough to do this week'),
      est_annual_value: z.number().nullable().describe('Dollars per year, only when it can be estimated from the data'),
      continues: z
        .string()
        .nullable()
        .describe('The exact title of an earlier recommendation this one restates or updates, copied from previous_recommendations; null if it is new'),
    }),
  ),
})

export type Advice = z.infer<typeof AdviceSchema>

const SYSTEM = `You are a senior financial planner (CFP-level) preparing a written review for one household. You receive an aggregated snapshot computed from the statements they uploaded, the profile facts known so far, and the recommendations already made. Write the way a good planner writes for a client who is financially literate: specific, quantified, no filler, no generic tips that would apply to anyone. Be pointed: when the answer is a product, name it — the card, the fund and its ticker, the bank account — and say why it beats the alternatives for this household, with the arithmetic. "Consider a cash-back card" is not a recommendation; "Apply for <card>: $X a year more than your current card on your $Y of groceries and $Z of everything else, after its $F fee" is.

Work through, in this order, and skip any area the data cannot support:
1. Balance sheet: net worth and its make-up (cash, taxable, retirement, HSA, debt), liquidity, concentration in any one account, institution or holding.
2. Cash flow: income, spending, savings rate, the trend across months, the largest outflows, recurring charges, fees and interest paid. Note that the window may be short.
3. Investments: overall allocation and whether it fits what you can infer about the household; asset location (bonds and REITs in tax-deferred accounts, broad equity index funds in taxable, highest expected growth in Roth); fund costs (weighted expense ratio); cash drag inside investment accounts; unrealised gains and losses (harvesting opportunities, low-basis positions).
4. Retirement and tax: account-specific rules — for example an inherited IRA's distribution deadline under the 10-year rule, Roth conversion windows, HSA used as a long-term investment account, contribution limits. Say which facts you would need (age, income, tax bracket, the date the IRA was inherited) when a conclusion depends on them.
5. Debt: rate against what safe cash and bonds earn now. Never recommend prepaying debt that costs less than a high-yield savings account pays.
6. Cash: idle balances against an emergency reserve of three to six months of spending, and what the excess could earn.
7. Risk: insurance coverage implied by the documents (an umbrella policy relative to net worth, for instance), beneficiaries and titling (TOD accounts), and a business account mixed with personal money.
8. Credit cards: spending.by_account shows what each card (and each checking account) paid for, by category and merchant, over the window. Work out what the current cards earn on that spending, using each card's actual reward terms. Then search for the cards that would earn the most on this household's mix — a flat 2% card, category cards for their largest categories, a store card where one merchant dominates — and compare the best one or two-card setup against what they hold now, net of annual fees and assuming the spending pattern continues. Name the cards. If the gain is under about $100 a year, say the current setup is fine and why. Point out spending paid from a checking account that could go on a card, and a card that should be closed (an annual fee it doesn't earn back).

Rules:
- Every figure about the household comes from the snapshot or documents_on_file; quote it so the reader can check you. Figures about a product or the market (a card's fee and reward rate, a savings yield, a fund's expense ratio) come from your web searches, with the source's URL. Every date is YYYY-MM-DD.
- spending.one_offs are purchases the person marked as one-off (a car bought outright, say). They are real money out but are left out of the spending totals, monthly averages and savings rate; treat them as capital outlays, not a spending habit. Categories already reflect the person's corrections.
- Accounts with "manual": true were entered by hand (a home's estimated value, a loan with no statement uploaded): treat them as the person's estimates, and say so when a conclusion rests on one.
- Profile rows with source "user" were set by the person and are true; never contradict them. Rows with source "inferred" are your earlier guesses: keep, revise or drop them, and add what you can now infer (for example target_allocation, emergency_fund_target_months, risk_capacity), each with its basis.
- You have web search. Use it to check the current terms of every product you name (a card's rewards and annual fee, a savings account's yield, a fund's expense ratio) and current market rates, rather than relying on memory; put the source's URL at the end of that recommendation's detail. Search only for what a recommendation needs; about ten searches is plenty. The final answer is the JSON alone.
- Do not repeat a recommendation whose status is "done" or "dismissed". You may restate one whose status is "new" if it still holds.
- Recommendations: five to ten, ordered by value to the household. Each one picks a course of action; when there are several reasonable choices, choose one and name the runner-up in a sentence. "high" means meaningful money or risk at stake now. est_annual_value is null unless it follows from the figures.
- documents_on_file lists every document already uploaded, newest first, with a summary of what it says (a pay stub's gross pay and deferrals, a grant's terms, a policy's coverage). Use the facts in those summaries, and cite the document. Never ask for a document that is already listed; if a listed document's summary lacks a figure you need, say which figure, not that the document is missing.
- When data is missing that would change a conclusion (a tax return, a policy's limits), say so in a recommendation with area "data", naming the exact document.
- Where a decision needs a CPA, attorney or insurance broker, say so plainly.`

/** The app's own guesses; every other Profile row was written by the person. */
export function isInferred(r: Row): boolean {
  return String(r.source ?? '').trim().toLowerCase() === 'inferred'
}

export interface AdviceRun {
  snapshot: Snapshot
  advice: Advice
  run_at: string
  /** The model that answered, which a server-side fallback can change. */
  model: string
}

/**
 * Every document on file, with the one-paragraph summary written when it was
 * read. The snapshot holds only what statements add up to, so without this a
 * pay stub, grant agreement or insurance page left no trace and the review
 * asked for documents that were already uploaded.
 */
export function documentInventory(documents: Row[]) {
  return documents
    .filter((d) => d.status === 'done' || d.status === 'review')
    .map((d) => ({
      date: d.period_end ?? null,
      type: d.doc_type ?? '',
      institution: d.institution ?? '',
      name: d.name ?? '',
      accounts: d.account_id ?? '',
      summary: d.summary ?? '',
    }))
    .sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')))
}

/** Where a review has got to, for the status line while it runs. */
export interface ReviewProgress {
  stage: 'reading' | 'thinking' | 'writing' | 'saving'
  /** What is happening now, in a few words. */
  note: string
  /** The latest heading from Claude's thinking summary, while it thinks. */
  thought?: string
}

/**
 * The latest heading in a thinking summary. Summaries are written as short
 * bold headings over a paragraph each; without a heading, the last sentence.
 */
export function latestThought(summary: string): string | undefined {
  const headings = [...summary.matchAll(/\*\*([^*\n]{3,120})\*\*/g)].map((m) => m[1].trim())
  if (headings.length) return headings.at(-1)
  const sentences = summary.trim().split(/(?<=[.!?])\s+/).filter(Boolean)
  const last = sentences.at(-1)
  return last && last.length > 160 ? `${last.slice(0, 157)}…` : last
}

/** How far the review's JSON has got: findings and recommendations written so far. */
export function writtenSoFar(json: string): { findings: number; recommendations: number } {
  const titles = (text: string) => text.match(/"title"\s*:/g)?.length ?? 0
  const recsAt = json.indexOf('"recommendations"')
  const findingsPart = json.slice(0, recsAt < 0 ? undefined : recsAt)
  return { findings: titles(findingsPart), recommendations: recsAt < 0 ? 0 : titles(json.slice(recsAt)) }
}

function writingNote(json: string): string {
  const { findings, recommendations } = writtenSoFar(json)
  const n = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`
  if (recommendations) return `Writing the review: ${n(findings, 'finding')}, ${n(recommendations, 'recommendation')} so far`
  if (findings) return `Writing the review: ${n(findings, 'finding')} so far`
  return 'Writing the review'
}

/** Runs the review and saves it: see review() and saveReview(). */
export async function advise(anthropic: Anthropic, db: Database, meter?: Meter, onProgress?: (p: ReviewProgress) => void): Promise<AdviceRun> {
  const run = await review(anthropic, db, meter, onProgress)
  await saveReview(db, run, onProgress)
  return run
}

/** Asks Claude for the review. Writes nothing but the Usage row. */
export async function review(anthropic: Anthropic, db: Database, meter?: Meter, onProgress?: (p: ReviewProgress) => void): Promise<AdviceRun> {
  onProgress?.({ stage: 'reading', note: 'Reading your spreadsheet' })
  const [tables, profile, recs] = await Promise.all([readTables(db), db.read('Profile'), db.read('Recommendations')])
  const snapshot = analyse(tables)

  const input = {
    today: formatDate(new Date()),
    snapshot,
    documents_on_file: documentInventory(tables.documents),
    profile: profile.rows.map((r) => ({ key: r.key, value: r.value, source: isInferred(r) ? 'inferred' : 'user', basis: r.basis })),
    previous_recommendations: recs.rows.map((r) => ({ area: r.area, title: r.title, status: r.status, created_at: r.created_at })),
  }

  const documents = input.documents_on_file.length
  onProgress?.({ stage: 'thinking', note: `Sent Claude the figures from ${documents} document${documents === 1 ? '' : 's'}; waiting for it to start` })

  const messages: Anthropic.Beta.BetaMessageParam[] = [{ role: 'user', content: `Snapshot and history as JSON:\n\n${JSON.stringify(input, null, 1)}` }]
  const usages: Anthropic.Beta.BetaUsage[] = []
  let message
  const started = performance.now()
  // Recorded even when a later turn fails: the turns before it were paid for.
  const record = (model: string) =>
    usages.length && meter?.({ kind: 'review', ref: 'Review', model, seconds: (performance.now() - started) / 1000, ...tokens(...usages) })
  try {
    // A turn with long-running searches can pause; it is resumed by sending it back.
    for (let turn = 0; ; turn++) {
      const stream = anthropic.beta.messages.stream({
        model: ADVICE_MODEL,
        // Thinking at high effort shares this budget with the answer; 32000 ran out.
        max_tokens: 64000,
        betas: ['server-side-fallback-2026-07-01'],
        fallbacks: 'default',
        // Summarized, so the status line can show what it is working through.
        thinking: { type: 'adaptive', display: 'summarized' },
        system: SYSTEM,
        tools: [{ type: 'web_search_20260209', name: 'web_search', max_uses: 12 }],
        output_config: { effort: 'high', format: betaZodOutputFormat(AdviceSchema) },
        messages,
      })
      stream.on('thinking', (_, summary) => onProgress?.({ stage: 'thinking', note: 'Claude is working through your figures', thought: latestThought(summary) }))
      stream.on('contentBlock', (block) => {
        const query = block.type === 'server_tool_use' && block.name === 'web_search' ? (block.input as { query?: string }).query : undefined
        if (query) onProgress?.({ stage: 'thinking', note: 'Checking current product terms', thought: `Searching: ${query}` })
      })
      stream.on('text', (_, json) => onProgress?.({ stage: 'writing', note: writingNote(json) }))
      message = await finish(stream)
      usages.push(message.usage)
      if (message.stop_reason !== 'pause_turn' || turn === 4) break
      messages.push({ role: 'assistant', content: message.content })
    }
  } catch (error) {
    record(ADVICE_MODEL)
    if (error instanceof Anthropic.APIError) throw new Error(`Claude API error ${error.status ?? ''}: ${error.message}`)
    throw new Error(`Could not reach Claude: ${(error as Error).message}`)
  }
  record(message.model)
  if (message.stop_reason === 'refusal') throw new Error('Claude declined to analyse this data')
  if (message.stop_reason === 'pause_turn') throw new Error('The review was still searching after five turns; nothing was saved')
  if (message.stop_reason === 'max_tokens') throw new Error('The review ran past its length limit before it finished; nothing was saved')
  if (!message.parsed_output) throw new Error('Claude returned an analysis that did not match the expected shape')
  return { snapshot, advice: message.parsed_output, run_at: formatTimestamp(new Date()), model: message.model }
}

const isClosedStatus = (r: Row) => ['done', 'dismissed'].includes(String(r.status ?? '').trim().toLowerCase())

/**
 * Writes a review. Recommendations and Profile are read again here, not at
 * the start of the review: the person can mark a recommendation done or edit
 * the Profile during the minutes Claude takes, and those edits must survive.
 * The Analyses row goes first: the likeliest failure, a cell over Sheets'
 * 50,000-character limit, then writes nothing at all. Safe to call again with
 * the same run after a failure (a sign-in that expired during the review).
 */
export async function saveReview(db: Database, run: AdviceRun, onProgress?: (p: ReviewProgress) => void): Promise<void> {
  const { advice, run_at: runAt } = run
  onProgress?.({ stage: 'saving', note: 'Saving the review' })
  // Appended, not rewritten: one row added, and a closed tab can't leave the
  // tab half-written. Skipped on a retry that got this far before.
  const analyses = await db.read('Analyses')
  if (!analyses.rows.some((r) => String(r.run_at) === runAt)) await db.append('Analyses', [{
    run_at: runAt,
    model: run.model,
    overview: advice.overview,
    findings_json: JSON.stringify(advice.findings),
    // What "What changed" compares the next review's recommendations against.
    recs_json: JSON.stringify(advice.recommendations.map((r) => ({ title: r.title, priority: r.priority, continues: r.continues }))),
  }])

  // Recommendations: keep what the person acted on; replace the open ones.
  onProgress?.({ stage: 'saving', note: 'Saving the recommendations' })
  await db.modify('Recommendations', (recs) => {
    recs.rows = [
      ...recs.rows.filter(isClosedStatus),
      ...advice.recommendations.map(
        (r, i): Row => ({
          rec_id: `${runAt}-${i + 1}`,
          created_at: runAt,
          area: r.area,
          priority: r.priority,
          title: r.title,
          detail: r.detail,
          next_step: r.next_step,
          est_annual_value: r.est_annual_value,
          status: 'new',
        }),
      ),
    ]
  })

  // Profile: a row the person added or edited is never overwritten. Only rows
  // marked "inferred" are the app's own, so a hand-added row with a blank
  // source is the person's too.
  await db.modify('Profile', (profile) => {
    const userKeys = new Set(profile.rows.filter((r) => !isInferred(r)).map((r) => String(r.key)))
    profile.rows = [
      ...profile.rows.filter((r) => !isInferred(r) && r.key),
      ...advice.profile
        .filter((p) => !userKeys.has(p.key))
        .map((p): Row => ({ key: p.key, value: p.value, source: 'inferred', basis: p.basis, updated_at: runAt })),
    ]
  })
}

export type Finding = Advice['findings'][number]

export interface Saved {
  overview: string
  findings: Finding[]
  run_at: string
  recommendations: Row[]
}

/** The last analysis as stored in the Sheet, so opening the app costs no Claude call. */
export async function lastAdvice(db: Database): Promise<Saved | undefined> {
  const [analyses, recs] = await Promise.all([db.read('Analyses'), db.read('Recommendations')])
  const last = analyses.rows.at(-1)
  if (!last) return undefined
  let findings: Finding[] = []
  try {
    findings = JSON.parse(String(last.findings_json ?? '[]'))
  } catch {
    // An analysis saved before findings existed, or a hand-edited cell.
  }
  return { overview: String(last.overview ?? ''), findings, run_at: String(last.run_at ?? ''), recommendations: recs.rows }
}

export async function setStatus(db: Database, recId: string, status: 'new' | 'done' | 'dismissed'): Promise<void> {
  await db.upsert('Recommendations', 'rec_id', [{ rec_id: recId, status }])
}
